import axios, { AxiosError } from 'axios';

// Which backend this build talks to. CRA bakes REACT_APP_* into the bundle at build
// time, so each environment gets its own build (see .gitlab-ci.yml) — the fallback is
// only the local-dev default.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://ai.staging.nuway.co';

// The backend asks for the partner token to live "for the tab session": sessionStorage,
// not localStorage, so closing the tab signs the partner out.
export const TOKEN_KEY = 'partnerToken';
export const USER_KEY = 'partnerUser';

export const getToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearSession = () => {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(USER_KEY);
  } catch {
    // storage unavailable — nothing to clear
  }
};

// Pages reachable without a session. A 401 on one of them is a wrong password or an
// expired link, not an ended session, so it must not bounce the user to /login.
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/accept-invitation'];

/** The backend's `{"detail": "..."}` message, which is written to be shown to the user. */
export const errorMessage = (err: unknown, fallback = 'Something went wrong. Please try again.'): string => {
  if (typeof err === 'string') return err;
  const detail = (err as any)?.detail;
  if (typeof detail === 'string') return detail;
  // 422: FastAPI sends a list of field errors.
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
};

// Any 401 on a /api/v1/partner/* call means the session ended (12 hour expiry, password
// reset, suspension): drop it and go back to sign-in. Rejects with `{ status, detail }`.
const errorHandler = (error: AxiosError<any>) => {
  const status = error?.response?.status;
  if (status === 401 && !PUBLIC_PATHS.includes(window.location.pathname)) {
    clearSession();
    window.location.assign('/login?expired=1');
  }
  const data = error?.response?.data;
  return Promise.reject({
    status,
    detail: data?.detail ?? (status ? undefined : 'Could not reach the server. Check your connection and try again.')
  });
};

const axiosServices = axios.create({ baseURL: API_BASE_URL });

axiosServices.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
axiosServices.interceptors.response.use((r) => r, errorHandler);

export default axiosServices;
